import {
  Key,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

import styled from '@mint-lib/styled';

import DrawerTab from '../../atoms/DrawerTab/index.js';
import DrawerTabPanel from '../../atoms/DrawerTabPanel/index.js';
import DrawerTabs from '../../molecules/DrawerTabs/index.js';

export type Tab<T> = {
  icon: JSX.Element;
  title: string;
  content: JSX.Element;
  value: T;
  dot?: JSX.Element;
  isHidden?: boolean;
  isNewData?: boolean;
  tooltip?: string;
};

type Props<T> = {
  value: T;
  tabs: Tab<T>[];
  tabsWithOverlay: T[];
  onChange: (value: T) => void;
  onClose?: () => void;
  className?: string;
};

function DrawerModal<T>({
  value,
  tabs,
  tabsWithOverlay,
  onChange,
  onClose,
  className,
}: Props<T>) {
  const [panelWidth, setPanelWidth] = useState<number>(0);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const filteredTabs = useMemo(
    () => tabs.filter((tab) => !tab.isHidden),
    [tabs],
  );

  useEffect(() => {
    if (panelRef.current && value) {
      setPanelWidth(panelRef.current?.clientWidth);
    } else {
      setPanelWidth(0);
    }
  }, [value]);

  return (
    <>
      <Overlay $isActive={tabsWithOverlay.includes(value)} onClick={onClose} />
      <Wrapper className={className} role={'drawer-modal'}>
        <TabsWrapper>
          <DrawerTabs<T> value={value} onChange={onChange}>
            {filteredTabs.map((tab) => {
              return (
                <DrawerTab
                  key={tab.value as Key}
                  icon={tab.icon}
                  value={tab.value}
                  isNewData={tab.isNewData}
                  tooltip={tab.tooltip}
                >
                  {tab.dot}
                </DrawerTab>
              );
            })}
          </DrawerTabs>
          <PanelWrapper $width={panelWidth}>
            {filteredTabs.map((tab) => (
              <DrawerTabPanel
                key={tab.value as Key}
                isActive={tab.value === value}
                panelRef={panelRef}
                title={tab.title}
                onClose={onClose}
              >
                {tab.content}
              </DrawerTabPanel>
            ))}
          </PanelWrapper>
        </TabsWrapper>
      </Wrapper>
    </>
  );
}

const Wrapper = styled('div')`
  position: fixed;
  z-index: 100;
  bottom: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.ui.primary};
  border-top: 1px solid ${({ theme }) => theme.palette.ui.tertiary};
  height: calc(100% - 47px);
`;

const Overlay = styled('div')<{ $isActive: boolean }>`
  position: fixed;
  z-index: 99;
  top: 60px;
  left: 56px;
  right: 0;
  bottom: 0;
  background-color: black;
  pointer-events: ${({ $isActive }) => ($isActive ? 'inherit' : 'none')};
  opacity: ${({ $isActive }) => ($isActive ? 0.1 : 0)};
  transition: opacity ease 0.3s;
`;

const TabsWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  height: 100%;
`;

const PanelWrapper = styled('div')<{ $width?: number }>`
  width: ${({ $width }) => ($width ? $width : 0)}px;
  transition: width ease 0.3s;
`;

export default memo(DrawerModal) as typeof DrawerModal;
